import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CurrentUserService } from '../../../core/services/user/current-user.service';
import { CustomersService } from '../../../core/services/customer/customers.service';
import { StatusUtils } from '../../../utils/status-utils';
import { UserSessionModel } from '../../../core/models/user/current-user.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InvoicesService } from '../../../core/services/invoice/invoices.service';
import { PaymentMethod } from '../../../../enums/payment-method.enum';
import { CaseStatus } from '../../../../enums/case-status.enum';
import { TablesComponent } from '../../components/tables/tables.component';
import { ColumnModel } from '../../../core/models/table/column.model';
import { FilterModel } from '../../../core/models/filters/filters.model';
import { DateFormatterUtil } from '../../../utils/date-format-utils';
import { SortModel } from '../../../core/models/filters/sort.model';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-invoice',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    TablesComponent,
  ],
  templateUrl: './invoice.component.html',
  styleUrl: './invoice.component.scss',
  providers: [DatePipe],
})
export class InvoiceComponent {
  @Output() iconChange = new EventEmitter<any>();
  currentUser: UserSessionModel | null = null;
  invoiceList: any = [];
  totalInvoice: number = 0;
  totalPage: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  currentPage: number = 1;
  invoiceListLoading: boolean = true;
  filterForm = {
    pid: '',
    nameSurname: '',
    startCreatedDate: null as string | null,
    endCreatedDate: null as string | null,
    customerCode: null as any,
    createdBy: '',
  };
  url: string = `${environment.apiLocations.patient}/PatientInvoice/PendingInvoiceList`;
  organizationList: { id: string; code: string }[] = [];
  caseStatuses = StatusUtils.getFilterCaseStatuses();
  maxDate: string;
  invoiceModalId: string = '';
  patientReport: string = '';
  pidNo: string = '';
  paymentMethod = PaymentMethod;
  patientRecordId: string = '';
  patientRecordCaseId: string = '';
  caseStatus = CaseStatus;
  reportError: string = '';
  invoiceReportLoading: boolean = false;
  invoiceReportGenerated: boolean = false;
  tableHeader: string = '';
  footerText: string = '';
  columns: ColumnModel[] = [];
  columnWidths: string[] = [
    'w-125',
    'w-200',
    'w-150',
    'w-125',
    'w-80',
  ];
  filters: FilterModel[] = [
    //AGB Code
    {
      type: 'input',
      label: 'dermNo',
      placeholder: 'filterByDermNo',
      filterId: 'PIDNo',
      value: null,
    },
    //StartDate
    {
      type: 'date',
      label: 'startCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'StartCreatedDateTime',
      value: null,
    },
    //EndCreateDate
    {
      type: 'date',
      label: 'endCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'EndCreatedDateTime',
      value: null,
    },
    //PatientName
    {
      type: 'input',
      label: 'patientName',
      placeholder: 'filterByNameAndSurname',
      filterId: 'NameSurname',
      value: null,
    },
    //CustomerCode
    {
      type: 'select',
      label: 'customerCode',
      placeholder: 'selectCustomerCode',
      filterId: 'CustomerCode',
      value: null,
    },
  ];
  sort: SortModel | null = null;

  constructor(
    private currentUserService: CurrentUserService,
    private customersService: CustomersService,
    private invoicesService: InvoicesService,
    private translate: TranslateService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.columns = [
      { key: 'pidNo', translateKey: 'dermNo' },
      { key: 'createdDate', translateKey: 'createdDate' },
      { key: 'patientName', translateKey: 'patientName' },
      { key: 'customerCode', translateKey: 'customerCode' },
      {
        key: 'actions',
        translateKey: 'actions',
        actionTypes: ['invoice'],
      },
    ];
    setTimeout(() => {
      this.tableHeader = this.translate.instant('invoiceList');
      this.footerText = this.translate.instant('footerText.invoice');
    }, 100);
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    // this.getInvoiceList(this.currentPage, this.pageSize);
    this.getOrganizations();
  }

  filterInvoiceList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getInvoiceList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getInvoiceList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getInvoiceList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  getSortByValue(column: string): string {
    switch (column) {
      case 'pidNo':
        return 'Patient.PidNo';
      case 'patientName':
        return 'Patient.Name';
      case 'patientStatus':
        return 'PatientRecordCases.PatientStatus';
      case 'createdBy':
        return 'CreatedUser';
      default:
        return column;
    }
  }

  getInvoiceList(
    page: number,
    size: number,
    filters?: any,
    sort?: SortModel
  ): void {
    this.invoiceListLoading = true;
    this.invoicesService.invoiceList(page, size, filters, sort).subscribe({
      next: (res) => {
        this.invoiceList = res.items;
        this.totalInvoice = res.count;
        this.totalPage = res.pages;
        this.pageSize = res.size;
        this.currentPage = page;
        this.invoiceListLoading = false;
      },
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getInvoiceList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getInvoiceList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  clearFilters() {
    this.filterForm.nameSurname = '';
    this.filterForm.pid = '';
    this.filterForm.startCreatedDate = null;
    this.filterForm.endCreatedDate = null;
    this.filterForm.customerCode = null;
    this.filterForm.createdBy = '';

    this.getInvoiceList(this.currentPage, this.pageSize);
  }

  changePage(page: number): void {
    if (page > 0 && page <= this.totalPage) {
      this.getInvoiceList(page, this.pageSize, this.filterForm);
    }
  }

  changePageSize(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const size = Number(selectElement.value);
    this.pageSize = size;
    this.currentPage = 1;
    this.getInvoiceList(this.currentPage, this.pageSize, this.filterForm);
  }

  getMiddlePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 1);
    const end = Math.min(this.totalPage - 1, this.currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  getOrganizations() {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
    });
  }
}
